import type { ParsingIssue } from "@/features/patient/records/api/dataPipelineApi";

export const ERROR_TYPE_LABELS: Record<string, string> = {
  MissingRequiredField: "Missing required information",
  MappingException: "Unrecognized data format",
  ValidationError: "Unrecognized data format",
  unsupported_format: "Unsupported file format",
};

// "Patient" isn't shown as a browsable resource anywhere else in the app (see
// parsedDataPresentation.ts's HIDDEN_RESOURCE_TYPES) — a successfully-parsed Patient
// record is just re-confirming the patient's own demographics, nothing new to show.
// But a *failed* Patient record can mean the uploaded file's identity doesn't match
// the authenticated patient, which is worth surfacing here — just under a label that
// doesn't read as "why is my own info an issue?".
export const RESOURCE_TYPE_LABELS: Record<string, string> = {
  Patient: "Identity Check",
};

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

/** Groups issues by (resource_type, source_file) and numbers each entry within a
 *  group ("2 of 3") so multiple genuinely distinct failed records of the same type
 *  from the same file don't render as indistinguishable duplicate cards. Also drops
 *  true duplicates — same record_id reported twice in the same group. */
export function prepareIssues(issues: ParsingIssue[]): (ParsingIssue & { ordinal?: string })[] {
  const groups = new Map<string, ParsingIssue[]>();
  const seenIdsByGroup = new Map<string, Set<string>>();

  for (const issue of issues) {
    const groupKey = `${issue.resource_type}|${issue.source_file ?? ""}`;
    if (issue.record_id) {
      const seenIds = seenIdsByGroup.get(groupKey) ?? new Set<string>();
      if (seenIds.has(issue.record_id)) continue;
      seenIds.add(issue.record_id);
      seenIdsByGroup.set(groupKey, seenIds);
    }
    const group = groups.get(groupKey) ?? [];
    group.push(issue);
    groups.set(groupKey, group);
  }

  return [...groups.values()].flatMap((group) =>
    group.map((issue, index) => (
      group.length > 1 ? { ...issue, ordinal: `${index + 1} of ${group.length}` } : issue
    ))
  );
}
